import { Building2, Clock, Users } from 'lucide-react'
import {
  getGroupTypesFromFilterConditions,
  getOfficeHoursFromFilterConditions,
  getOfficeHoursLabel,
  getPropertyGroupIdsFromFilterConditions,
  getPropertyIdsFromFilterConditions,
  OfficeHoursMode,
  type FilterCondition,
} from '@super/shared-types'

const titleCase = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()

export function propertyScopeLabel(opts: {
  propertyCount: number
  propertyGroupCount: number
  /** Shown instead of "1 property" when exactly one property is selected. */
  singlePropertyName?: string
}): string {
  const { propertyCount, propertyGroupCount, singlePropertyName } = opts
  const groups =
    propertyGroupCount === 1
      ? '1 property group'
      : `${propertyGroupCount} property groups`
  const properties =
    propertyCount === 1
      ? singlePropertyName || '1 property'
      : `${propertyCount} properties`
  if (propertyGroupCount > 0 && propertyCount > 0)
    return `${groups} + ${properties}`
  if (propertyGroupCount > 0) return groups
  if (propertyCount > 0) return properties
  return 'All properties'
}

// Shared conditions cell for the scenarios / actions / knowledge lists: one
// stacked row per condition dimension so every list reads the same way.
export function FilterConditionsSummary(props: {
  officeHours?: OfficeHoursMode
  /** Knowledge bases have no office-hours dimension — they pass false. */
  showOfficeHours?: boolean
  propertyCount: number
  propertyGroupCount: number
  singlePropertyName?: string
  groupTypes: string[]
}) {
  const rows: Array<{ icon: typeof Clock; label: string }> = [
    ...(props.showOfficeHours === false
      ? []
      : [
          {
            icon: Clock,
            label: getOfficeHoursLabel(
              props.officeHours ?? OfficeHoursMode.ALWAYS,
            ),
          },
        ]),
    {
      icon: Building2,
      label: propertyScopeLabel(props),
    },
    {
      icon: Users,
      label:
        props.groupTypes.length > 0
          ? props.groupTypes.map(titleCase).join(', ')
          : 'All groups',
    },
  ]
  return (
    <div className="flex flex-col gap-1">
      {rows.map(({ icon: Icon, label }) => (
        <div
          key={label}
          className="flex items-center gap-1 text-xs text-muted-foreground"
        >
          <Icon className="h-3 w-3 shrink-0" />
          <span>{label}</span>
        </div>
      ))}
    </div>
  )
}

/** Variant that parses raw filterConditions (scenarios list). */
export function FilterConditionsSummaryFromConditions({
  filterConditions,
}: {
  filterConditions?: FilterCondition[] | null
}) {
  return (
    <FilterConditionsSummary
      officeHours={getOfficeHoursFromFilterConditions(filterConditions)}
      propertyCount={
        getPropertyIdsFromFilterConditions(filterConditions).length
      }
      propertyGroupCount={
        getPropertyGroupIdsFromFilterConditions(filterConditions).length
      }
      groupTypes={getGroupTypesFromFilterConditions(filterConditions)}
    />
  )
}
